/**
 * QR Code Generator - Enhanced QR code generation for shortened URLs
 *
 * This module provides QR code generation functionality with customization options
 * optimized for URL sharing and enhanced user experience.
 *
 * Features:
 * - Multiple output formats (SVG, PNG, JPEG, WebP)
 * - Customizable colors and branding
 * - Logo integration
 * - Error correction levels
 * - Batch generation
 * - Cross-platform compatibility
 *
 * @author ShortURL Team
 * @version 1.0.0
 */

import QRCodeStyling from 'qr-code-styling'

/**
 * QR Code generation options
 */
export interface QRCodeOptions {
  // Basic options
  size?: number
  format?: 'svg' | 'png' | 'jpeg' | 'webp'
  errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H'

  // Visual customization
  foregroundColor?: string
  backgroundColor?: string

  // Logo/branding
  logoUrl?: string
  logoSize?: number
  logoBackgroundColor?: string
  logoMargin?: number

  // Advanced styling
  cornerStyle?: 'square' | 'rounded' | 'extra-rounded'
  dotStyle?: 'square' | 'rounded' | 'classy' | 'classy-rounded'

  // Frame and border
  frameText?: string
  frameColor?: string
  borderSize?: number

  // Output options
  quality?: number
  margin?: number
}

/**
 * QR Code generation result
 */
export interface QRCodeResult {
  dataUrl: string
  blob?: Blob
  svg?: string
  size: number
  format: string
  generatedAt: Date
}

/**
 * Batch QR Code generation options
 */
export interface BatchQRCodeOptions extends QRCodeOptions {
  urls: string[]
  filenamePrefix?: string
}

/**
 * Default QR code configuration
 */
const DEFAULT_QR_OPTIONS: Required<QRCodeOptions> = {
  size: 300,
  format: 'png',
  errorCorrectionLevel: 'M',
  foregroundColor: '#000000',
  backgroundColor: '#ffffff',
  logoUrl: '',
  logoSize: 50,
  logoBackgroundColor: '#ffffff',
  logoMargin: 10,
  cornerStyle: 'square',
  dotStyle: 'square',
  frameText: '',
  frameColor: '#000000',
  borderSize: 0,
  quality: 0.9,
  margin: 10,
}

/**
 * Predefined QR code styles for different use cases
 */
const QR_CODE_PRESETS = {
  minimal: {
    size: 200,
    format: 'svg' as const,
    foregroundColor: '#000000',
    backgroundColor: '#ffffff',
    cornerStyle: 'square' as const,
    dotStyle: 'square' as const,
    margin: 5,
  },

  modern: {
    size: 300,
    format: 'png' as const,
    foregroundColor: '#2563eb',
    backgroundColor: '#ffffff',
    cornerStyle: 'rounded' as const,
    dotStyle: 'rounded' as const,
    margin: 15,
  },

  branded: {
    size: 400,
    format: 'png' as const,
    foregroundColor: '#1f2937',
    backgroundColor: '#f9fafb',
    cornerStyle: 'extra-rounded' as const,
    dotStyle: 'classy-rounded' as const,
    logoSize: 60,
    logoMargin: 15,
    margin: 20,
  },

  print: {
    size: 600,
    format: 'png' as const,
    foregroundColor: '#000000',
    backgroundColor: '#ffffff',
    cornerStyle: 'square' as const,
    dotStyle: 'square' as const,
    quality: 1.0,
    margin: 30,
  },

  social: {
    size: 400,
    format: 'png' as const,
    foregroundColor: '#059669',
    backgroundColor: '#ecfdf5',
    cornerStyle: 'rounded' as const,
    dotStyle: 'classy' as const,
    margin: 20,
  },
}

/**
 * Creates a QR code styling configuration
 *
 * @param url - URL to encode
 * @param options - QR code options
 * @returns QR code styling configuration
 */
/** Blob 을 <img src> 에 바로 쓸 수 있는 data URL 로 바꾼다 */
function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(new Error('Failed to read QR code data'))
    reader.readAsDataURL(blob)
  })
}

function createQRCodeConfig(url: string, options: QRCodeOptions) {
  const config = { ...DEFAULT_QR_OPTIONS, ...options }

  const qrConfig: any = {
    width: config.size,
    height: config.size,
    type: config.format === 'svg' ? 'svg' : 'canvas',
    data: url,
    margin: config.margin,
    qrOptions: {
      typeNumber: 0,
      mode: 'Byte',
      errorCorrectionLevel: config.errorCorrectionLevel,
    },
    imageOptions: {
      hideBackgroundDots: true,
      imageSize: config.logoSize / config.size,
      margin: config.logoMargin,
      crossOrigin: 'anonymous',
    },
    dotsOptions: {
      color: config.foregroundColor,
      type: config.dotStyle,
    },
    backgroundOptions: {
      color: config.backgroundColor,
    },
    cornersSquareOptions: {
      color: config.foregroundColor,
      type: config.cornerStyle,
    },
    cornersDotOptions: {
      color: config.foregroundColor,
      type: config.cornerStyle,
    },
  }

  // Add logo if provided
  if (config.logoUrl) {
    qrConfig.image = config.logoUrl
    qrConfig.imageOptions.imageSize = config.logoSize / config.size
    qrConfig.imageOptions.margin = config.logoMargin
  }

  return qrConfig
}

/**
 * Generates a QR code for a given URL
 *
 * @param url - URL to encode in QR code
 * @param options - QR code generation options
 * @returns Promise resolving to QR code result
 */
export async function generateQRCode(
  url: string,
  options: QRCodeOptions = {}
): Promise<QRCodeResult> {
  if (!url) {
    throw new Error('URL is required for QR code generation')
  }

  const config = createQRCodeConfig(url, options)
  const qrCode = new QRCodeStyling(config)

  try {
    let dataUrl: string
    let blob: Blob | undefined
    let svg: string | undefined

    if (options.format === 'svg') {
      // Generate SVG
      const rawData = await qrCode.getRawData('svg')
      if (typeof rawData === 'string') {
        svg = rawData
      } else {
        throw new Error('Invalid SVG data received')
      }
      const svgBlob = new Blob([svg], { type: 'image/svg+xml' })
      dataUrl = URL.createObjectURL(svgBlob)
    } else {
      // qrCode.append(el) 은 el 을 '담을 그릇'으로 보고 그 안에 자기 캔버스를
      // 새로 넣는다. 예전에는 빈 <canvas> 를 넘겨 캔버스 안에 캔버스를 넣은
      // 꼴이 되어, 겉 캔버스는 비어 있었고 toDataURL 이 빈 그림을 돌려줬다.
      // 라이브러리가 직접 그려 주는 데이터를 받는다.
      const format = options.format || DEFAULT_QR_OPTIONS.format
      const rawData = await qrCode.getRawData(format)
      if (!rawData) {
        throw new Error('Empty QR code data received')
      }

      blob =
        rawData instanceof Blob
          ? rawData
          : new Blob([rawData as unknown as BlobPart], {
              type: `image/${format}`,
            })
      dataUrl = await blobToDataUrl(blob)
    }

    return {
      dataUrl,
      blob,
      svg,
      size: options.size || DEFAULT_QR_OPTIONS.size,
      format: options.format || DEFAULT_QR_OPTIONS.format,
      generatedAt: new Date(),
    }
  } catch (error) {
    throw new Error(
      `QR code generation failed: ${error instanceof Error ? error.message : 'Unknown error'}`
    )
  }
}

/**
 * Generates QR codes for multiple URLs
 *
 * @param options - Batch generation options
 * @returns Promise resolving to array of QR code results
 */
export async function generateBatchQRCodes(
  options: BatchQRCodeOptions
): Promise<QRCodeResult[]> {
  if (!options.urls || options.urls.length === 0) {
    throw new Error('At least one URL is required for batch generation')
  }

  const results: QRCodeResult[] = []

  for (const url of options.urls) {
    try {
      const result = await generateQRCode(url, options)
      results.push(result)
    } catch (error) {
      console.error(`Failed to generate QR code for ${url}:`, error)
      // Continue with other URLs
    }
  }

  return results
}

/**
 * Downloads a QR code as a file
 *
 * @param qrResult - QR code result to download
 * @param filename - Optional filename
 */
export function downloadQRCode(
  qrResult: QRCodeResult,
  filename?: string
): void {
  const defaultFilename = `qr-code-${Date.now()}.${qrResult.format}`
  const finalFilename = filename || defaultFilename

  const link = document.createElement('a')
  link.href = qrResult.dataUrl
  link.download = finalFilename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

/**
 * Copies QR code to clipboard
 *
 * @param qrResult - QR code result to copy
 * @returns Promise resolving when copy is complete
 */
export async function copyQRCodeToClipboard(
  qrResult: QRCodeResult
): Promise<void> {
  if (!qrResult.blob) {
    throw new Error('Blob data is required for clipboard copy')
  }

  try {
    const clipboardItem = new ClipboardItem({
      [qrResult.blob.type]: qrResult.blob,
    })

    await navigator.clipboard.write([clipboardItem])
  } catch (error) {
    throw new Error(
      `Failed to copy QR code to clipboard: ${error instanceof Error ? error.message : 'Unknown error'}`
    )
  }
}

/**
 * Validates QR code options
 *
 * @param options - Options to validate
 * @returns Validation result
 */
export function validateQRCodeOptions(options: QRCodeOptions): {
  isValid: boolean
  errors: string[]
} {
  const errors: string[] = []

  if (options.size !== undefined) {
    if (options.size < 100 || options.size > 2000) {
      errors.push('Size must be between 100 and 2000 pixels')
    }
  }

  if (options.quality !== undefined) {
    if (options.quality < 0.1 || options.quality > 1.0) {
      errors.push('Quality must be between 0.1 and 1.0')
    }
  }

  if (options.logoSize !== undefined && options.size !== undefined) {
    if (options.logoSize > options.size * 0.3) {
      errors.push('Logo size should not exceed 30% of QR code size')
    }
  }

  if (options.foregroundColor && options.backgroundColor) {
    if (options.foregroundColor === options.backgroundColor) {
      errors.push('Foreground and background colors must be different')
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  }
}

/**
 * Gets estimated file size for QR code options
 *
 * @param options - QR code options
 * @returns Estimated file size in bytes
 */
export function estimateQRCodeSize(options: QRCodeOptions): number {
  const size = options.size || DEFAULT_QR_OPTIONS.size
  const format = options.format || DEFAULT_QR_OPTIONS.format

  // Rough estimates based on format and size
  const pixelCount = size * size

  switch (format) {
    case 'svg':
      // SVG is typically much smaller
      return Math.round(pixelCount * 0.01) // ~1% of pixel count

    case 'png':
      // PNG with compression
      return Math.round(pixelCount * 0.5) // ~50% of pixel count

    case 'jpeg':
      // JPEG with compression
      return Math.round(pixelCount * 0.2) // ~20% of pixel count

    case 'webp':
      // WebP with better compression
      return Math.round(pixelCount * 0.15) // ~15% of pixel count

    default:
      return Math.round(pixelCount * 0.5)
  }
}

/**
 * Creates a shareable QR code with metadata
 *
 * @param url - URL to encode
 * @param options - QR code options
 * @param metadata - Additional metadata
 * @returns Promise resolving to enhanced QR code result
 */
export async function createShareableQRCode(
  url: string,
  options: QRCodeOptions = {},
  metadata: {
    title?: string
    description?: string
    createdBy?: string
  } = {}
): Promise<QRCodeResult & { metadata: typeof metadata }> {
  const qrResult = await generateQRCode(url, options)

  return {
    ...qrResult,
    metadata: {
      title: metadata.title || 'QR Code',
      description: metadata.description || `QR code for ${url}`,
      createdBy: metadata.createdBy || 'ShortURL',
      ...metadata,
    },
  }
}

/**
 * Optimizes QR code options for specific use cases
 *
 * @param useCase - Use case identifier
 * @param customOptions - Custom options to merge
 * @returns Optimized options
 */
export function optimizeQRCodeForUseCase(
  useCase: 'web' | 'print' | 'mobile' | 'social' | 'email',
  customOptions: QRCodeOptions = {}
): QRCodeOptions {
  let baseOptions: QRCodeOptions

  switch (useCase) {
    case 'web':
      baseOptions = {
        size: 200,
        format: 'svg',
        margin: 5,
        errorCorrectionLevel: 'M',
      }
      break

    case 'print':
      baseOptions = {
        size: 600,
        format: 'png',
        quality: 1.0,
        margin: 30,
        errorCorrectionLevel: 'H',
        foregroundColor: '#000000',
        backgroundColor: '#ffffff',
      }
      break

    case 'mobile':
      baseOptions = {
        size: 300,
        format: 'png',
        margin: 10,
        errorCorrectionLevel: 'M',
        cornerStyle: 'rounded',
        dotStyle: 'rounded',
      }
      break

    case 'social':
      baseOptions = {
        size: 400,
        format: 'png',
        margin: 20,
        errorCorrectionLevel: 'M',
        cornerStyle: 'extra-rounded',
        dotStyle: 'classy-rounded',
      }
      break

    case 'email':
      baseOptions = {
        size: 250,
        format: 'png',
        quality: 0.8,
        margin: 15,
        errorCorrectionLevel: 'M',
      }
      break

    default:
      baseOptions = {}
  }

  return { ...baseOptions, ...customOptions }
}

/**
 * Checks if QR code generation is supported in current environment
 *
 * @returns Whether QR code generation is supported
 */
export function isQRCodeSupported(): boolean {
  if (typeof document === 'undefined') {
    return false
  }

  try {
    // Check for required APIs
    const hasDocument = !!document.createElement
    const hasToDataURL = !!HTMLCanvasElement.prototype.toDataURL
    const hasToBlob = !!HTMLCanvasElement.prototype.toBlob

    return hasDocument && hasToDataURL && hasToBlob
  } catch {
    return false
  }
}

/**
 * Gets QR code color schemes for different themes
 */
export const QR_COLOR_SCHEMES = {
  light: {
    foregroundColor: '#000000',
    backgroundColor: '#ffffff',
  },
  dark: {
    foregroundColor: '#ffffff',
    backgroundColor: '#000000',
  },
  brand: {
    foregroundColor: '#2563eb',
    backgroundColor: '#ffffff',
  },
  success: {
    foregroundColor: '#059669',
    backgroundColor: '#ecfdf5',
  },
  warning: {
    foregroundColor: '#d97706',
    backgroundColor: '#fffbeb',
  },
  error: {
    foregroundColor: '#dc2626',
    backgroundColor: '#fef2f2',
  },
}

// Export utility functions
export { DEFAULT_QR_OPTIONS, QR_CODE_PRESETS }
